import { useDesignerStore } from '../../stores';
import { useCanvasOperations } from '../../hooks/useCanvasOperations';
import { useHistory } from '../../hooks/useHistory';
import { UploadPanel } from '../common/UploadPanel';
import type { ToolType, ShapeType } from '../../types/tools';
import { t } from '../../i18n';
import styles from './Toolbar.module.css';
import { useState, useRef, useEffect } from 'react';

// Icons as simple SVG components
const SelectIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3l7.07 16.97 2.51-7.39 7.39-2.51L3 3z" />
  </svg>
);

const TextIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="4 7 4 4 20 4 20 7" />
    <line x1="9" y1="20" x2="15" y2="20" />
    <line x1="12" y1="4" x2="12" y2="20" />
  </svg>
);

const ImageIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const ShapesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="8" height="8" />
    <circle cx="17" cy="7" r="4" />
    <polygon points="12 14 7 22 17 22" />
  </svg>
);

const UndoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const RedoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

interface ShapeOption {
  type: ShapeType;
  label: string;
  icon: React.ReactNode;
}

const shapeOptions: ShapeOption[] = [
  {
    type: 'rect',
    label: 'Rectangle',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
      </svg>
    ),
  },
  {
    type: 'circle',
    label: 'Circle',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
      </svg>
    ),
  },
  {
    type: 'triangle',
    label: 'Triangle',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 22 22 2 22" />
      </svg>
    ),
  },
  {
    type: 'line',
    label: 'Line',
    icon: (
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="4" y1="20" x2="20" y2="4" />
      </svg>
    ),
  },
];

export const Toolbar = () => {
  const { currentTool, setTool, selectedObjectIds } = useDesignerStore();
  const { addText, addShape, deleteSelected } = useCanvasOperations();
  const { undo, redo, canUndo, canRedo } = useHistory();
  const [showShapesDropdown, setShowShapesDropdown] = useState(false);
  const [showUploadPanel, setShowUploadPanel] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const uploadPanelRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowShapesDropdown(false);
      }
      if (uploadPanelRef.current && !uploadPanelRef.current.contains(event.target as Node)) {
        const target = event.target as HTMLElement;
        // Don't close if clicking the upload button
        if (!target.closest('[data-tool="image"]')) {
          setShowUploadPanel(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToolClick = (tool: ToolType) => {
    setTool(tool);
    if (tool === 'text') {
      addText();
      setTool('select');
    }
  };

  const handleShapeClick = (shapeType: ShapeType) => {
    addShape(shapeType);
    setShowShapesDropdown(false);
    setTool('select');
  };

  const handleImageClick = () => {
    setShowUploadPanel(!showUploadPanel);
  };

  const hasSelection = selectedObjectIds.length > 0;

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolGroup}>
        <button
          className={`${styles.toolButton} ${currentTool === 'select' ? styles.active : ''}`}
          onClick={() => handleToolClick('select')}
          title={t('design.tool.select', 'Select (V)')}
        >
          <SelectIcon />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolGroup}>
        <button
          className={`${styles.toolButton} ${currentTool === 'text' ? styles.active : ''}`}
          onClick={() => handleToolClick('text')}
          title={t('design.tool.add_text', 'Add Text (T)')}
        >
          <TextIcon />
        </button>

        <div ref={uploadPanelRef} style={{ position: 'relative' }}>
          <button
            className={`${styles.toolButton} ${showUploadPanel ? styles.active : ''}`}
            onClick={handleImageClick}
            title={t('design.tool.add_image', 'Add Image (I)')}
            data-tool="image"
          >
            <ImageIcon />
          </button>
          <UploadPanel isOpen={showUploadPanel} onClose={() => setShowUploadPanel(false)} />
        </div>

        <div className={styles.dropdownContainer} ref={dropdownRef}>
          <button
            className={`${styles.toolButton} ${currentTool === 'shapes' ? styles.active : ''}`}
            onClick={() => setShowShapesDropdown(!showShapesDropdown)}
            title={t('design.tool.add_shape', 'Add Shape (S)')}
          >
            <ShapesIcon />
          </button>

          {showShapesDropdown && (
            <div className={styles.dropdown}>
              {shapeOptions.map((shape) => (
                <button
                  key={shape.type}
                  className={styles.dropdownItem}
                  onClick={() => handleShapeClick(shape.type)}
                >
                  {shape.icon}
                  <span>{shape.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolGroup}>
        <button
          className={styles.toolButton}
          onClick={undo}
          disabled={!canUndo}
          title={t('design.tool.undo', 'Undo (Ctrl+Z)')}
        >
          <UndoIcon />
        </button>
        <button
          className={styles.toolButton}
          onClick={redo}
          disabled={!canRedo}
          title={t('design.tool.redo', 'Redo (Ctrl+Y)')}
        >
          <RedoIcon />
        </button>
      </div>

      <div className={styles.divider} />

      <div className={styles.toolGroup}>
        <button
          className={styles.toolButton}
          onClick={deleteSelected}
          disabled={!hasSelection}
          title={t('design.tool.delete', 'Delete (Del)')}
        >
          <DeleteIcon />
        </button>
      </div>
    </div>
  );
};

export default Toolbar;
